import { trackInitiateCheckout as trackInitiateCheckoutMeta, getMetaTrackingData, isBrowser } from "@/lib/meta";
import { trackBeginCheckout as trackBeginCheckoutGA4 } from "@/lib/gtag";
import { track, EVENTS } from "@/lib/mixpanel";
import { getStripeLookupKey } from "@/lib/checkout";
import type { PlanId } from "@/lib/checkout";
import { getCurrencyFromSymbol } from "@/lib/currencies";

export interface PlanData {
  id: string;
  pricing: {
    current: number;
    currency: string;
  };
}

interface TrackingOptions {
  eventCode?: string;
  referralCode?: string;
}

export function useTracking() {
  const trackCheckout = (plan: PlanData, options?: TrackingOptions) => {
    const currency = getCurrencyFromSymbol(plan.pricing.currency);
    const lookupKey = getStripeLookupKey(plan.id as PlanId);
    const eventCode = options?.eventCode;
    const referralCode = options?.referralCode;

    // Mixpanel tracking (existing)
    track(EVENTS.INITIATE_PURCHASE, {
      planId: plan.id,
      planPrice: plan.pricing.current,
      currency,
      eventCode: eventCode,
      referralCode: referralCode,
      stripeLookupKey: lookupKey
    });

    // Meta InitiateCheckout tracking
    if (isBrowser()) {
      const metaTracking = getMetaTrackingData();

      // Track InitiateCheckout with available data (including user data if signed in)
      trackInitiateCheckoutMeta(
        plan.pricing.current,
        currency,
        metaTracking.event_id
      );

      // Store event_id for backend enrichment (IP address, etc.)
      // getMetaTrackingData() already handles reading from sessionStorage,
      // but we store it here to ensure it's saved after generation
      window.sessionStorage.setItem('initiate_checkout_event_id', metaTracking.event_id);
      window.sessionStorage.setItem('initiate_checkout_plan_id', plan.id);
    }

    // GA4 begin_checkout tracking
    trackBeginCheckoutGA4({
      value: plan.pricing.current,
      currency,
      items: [{
        item_id: lookupKey,
        item_name: plan.id,
        price: plan.pricing.current,
        quantity: 1,
      }],
      ...(eventCode && { promotion_id: eventCode }),
      ...(referralCode && { coupon: referralCode }),
    });
  };

  return {
    trackCheckout
  };
}
